import { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSWRMutation from 'swr/mutation';

import { ConfirmationDialog } from '~components';
import { pagePathSegments } from '~constants';
import { useSnackbar } from '~hooks';
import { MutationRefProps } from '~interfaces/refProps';

import UserGroupsDataGrid from '../components/dataGrids/userGroupsDataGrid';
import UserGroupsService from '../services/userGroupsService';

const service = new UserGroupsService();

type UserAction =
	| {
			type: 'create';
			id?: never;
	  }
	| {
			type: 'delete';
			id: string;
	  };

const UserGroupsOverviewContainer = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { enqueueSuccessSnackbar, enqueueAxiosErrorSnackbar } = useSnackbar();
	const dataGridRef = useRef<MutationRefProps>(null);

	const [userAction, setUserAction] = useState<UserAction | null>(null);

	const { trigger: triggerDelete, isMutating: isDeleteMutating } = useSWRMutation(
		userAction?.id ? [service.basePath, userAction.id] : null,
		([_, id]) => service.deleteUserGroup(id),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(t('deleted'));
				setUserAction(null);
				dataGridRef.current?.mutate();
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	return (
		<>
			<UserGroupsDataGrid
				ref={dataGridRef}
				onCreate={() => navigate(pagePathSegments.Add)}
				onDelete={(id) => setUserAction({ type: 'delete', id: id })}
			/>
			<ConfirmationDialog
				open={userAction?.type === 'delete'}
				loading={isDeleteMutating}
				onConfirm={async () => await triggerDelete()}
				onClose={() => setUserAction(null)}
			/>
		</>
	);
};

export default UserGroupsOverviewContainer;
